import { useCallback } from 'react';

import { useNavigate } from 'react-router-dom';

import { ICartEntry } from '@rbi-ctg/menu';
import { useRedeemReward } from 'components/cart-item/redeem-reward/use-redeem-reward/use-redeem-reward';
import { IncrementorActions } from 'components/incrementor';
import { useLoyaltyContext } from 'state/loyalty';
import { useOffersContext } from 'state/offers';
import { useOrderContext } from 'state/order';
import { generateCartItemUrl } from 'utils/routing';

export const useCartItemEditingTools = ({
  item,
  isOffer,
}: {
  item: ICartEntry;
  isOffer?: boolean;
}) => {
  const { removeFromCart, updateQuantity } = useOrderContext();
  const { clearSelectedOffer } = useOffersContext();
  const navigate = useNavigate();
  const { loyaltyEnabled, unApplyReward, appliedLoyaltyRewards, applyReward } = useLoyaltyContext();
  const { incentiveNotInMenu } = useRedeemReward(item);

  const handleEditItem = useCallback(() => {
    // If its a reorder item always take directly to the item. Only allow picker changes if we haven't ordered it before.
    const baseUrl = item.reorder
      ? `/menu/${item.type.toLowerCase()}-${item._id}`
      : item.url.split('?')[0];
    const itemUrl = generateCartItemUrl({
      id: item._id,
      isOffer,
      cartId: item.cartId,
      children: item.children,
      quantity: item.quantity,
      pickerSelections: item.pickerSelections,
      location: { pathname: baseUrl },
    });
    navigate(itemUrl);
  }, [
    isOffer,
    item._id,
    item.cartId,
    item.children,
    item.quantity,
    item.pickerSelections,
    item.reorder,
    item.type,
    item.url,
    navigate,
  ]);

  const handleRemoveItem = useCallback(() => {
    isOffer ? clearSelectedOffer() : removeFromCart(item.cartId);
  }, [clearSelectedOffer, isOffer, item.cartId, removeFromCart]);

  const handleQuantityChange = useCallback(
    (quantity: number, incrementorAction: IncrementorActions) => {
      if (loyaltyEnabled) {
        const numberOfAppliedRewards = appliedLoyaltyRewards[item.cartId]?.timesApplied;
        const quantityLessThanAppliedRewards = quantity < numberOfAppliedRewards;
        if (quantityLessThanAppliedRewards) {
          unApplyReward({
            rewardBenefitId: item._id,
            cartId: item.cartId,
          });
        }
        // if incentive is not in menu, any quantity update should apply or remove the incentive
        if (incentiveNotInMenu && incrementorAction === IncrementorActions.Increment) {
          applyReward({ rewardBenefitId: item._id, cartId: item.cartId });
        }
      }

      updateQuantity(item.cartId, quantity);
    },
    [
      appliedLoyaltyRewards,
      applyReward,
      incentiveNotInMenu,
      item._id,
      item.cartId,
      loyaltyEnabled,
      unApplyReward,
      updateQuantity,
    ]
  );

  return {
    handleEditItem,
    handleRemoveItem,
    handleQuantityChange,
  };
};
